import React, { useCallback, useState } from 'react';
import CommonSelect from '../../common/select/CommonSelect';
import InputWithLabel from '../../common/InputWithLabel';
import TextareaWithLabel from '../../common/TextareaWithLabel';
import LazyLoadImageProp from '../../common/LazyLoadImage';
import DragDropFileInput from '../../common/DragDropFileInput';
import { XMarkIcon } from '@heroicons/react/20/solid';
import Icons from '../../svgs/icons';
import { useWorkoutPlanCreation } from '../../../context/WorkoutPlanContext';
import { errorToast, successToast } from '../../../utils/helper';

const DIFFICULTY_LEVELS = [
  { label: 'Beginner', value: 'Beginner' },
  { label: 'Intermediate', value: 'Intermediate' },
  { label: 'Advanced', value: 'Advanced' }
];

const LOCATIONS = [
  { label: 'Home', value: 'Home' },
  { label: 'Gym', value: 'Gym' },
  { label: 'Outdoor', value: 'Outdoor' }
];

const FOCUS_OPTIONS = [
  { label: 'Strength', value: 'Strength' },
  { label: 'Sculpt', value: 'Sculpt' },
  { label: 'Conditioning', value: 'Conditioning' },
  { label: 'Athleticism', value: 'Athleticism' },
  { label: 'Fundamentals', value: 'Fundamentals' },
  { label: 'Size', value: 'Size' }
];

const WorkoutPlanDetailsTab = ({ isView }) => {
  // Use context only
  const { planData, errors, updatePlanData } = useWorkoutPlanCreation();

  // Local state for thumbnail preview
  const [previewThumbnailImage, setPreviewThumbnailImage] = useState('');
  const [uploadingThumbnail, setUploadingThumbnail] = useState(false);
  const [userUploadedNewImage, setUserUploadedNewImage] = useState(false);

  // Update preview when planData thumbnail_url changes (only if user hasn't uploaded new image)
  React.useEffect(() => {
    if (planData.thumbnail_url && !userUploadedNewImage) {
      setPreviewThumbnailImage(planData.thumbnail_url);
    }
  }, [planData.thumbnail_url, userUploadedNewImage]);

  // Handle form field changes
  const handleChange = useCallback(
    (e) => {
      const { name, value } = e.target;

      updatePlanData({
        [name]: value
      });
    },
    [updatePlanData]
  );

  // Handle select field changes
  const handleSelectChange = useCallback(
    (field, selectedOption) => {
      const value = selectedOption?.value || '';
      updatePlanData({
        [field]: value
      });
    },
    [updatePlanData]
  );

  // Handle thumbnail upload
  const handleThumbnailUpload = useCallback(
    async (file) => {
      if (!file) return;

      if (!/\.(jpg|jpeg|png|gif)$/i.test(file.name)) {
        errorToast('Please upload a valid image file (JPG, JPEG, PNG, GIF)');
        return;
      }

      if (file.size > 1024 * 1024) {
        errorToast('Image file size should be less than 1MB');
        return;
      }

      try {
        setUploadingThumbnail(true);
        const localPreview = URL.createObjectURL(file);
        setPreviewThumbnailImage(localPreview);
        setUserUploadedNewImage(true);

        // Store file locally, will upload via presigned URL from API response
        updatePlanData({
          thumbnailFile: file,
          thumbnail_url: localPreview
        });

        successToast('Thumbnail selected successfully');
      } catch (error) {
        console.error('Thumbnail selection error:', error);
        errorToast('Failed to select thumbnail');
        updatePlanData({
          thumbnailFile: null,
          thumbnail_url: ''
        });
        setPreviewThumbnailImage('');
        setUserUploadedNewImage(false);
      } finally {
        setUploadingThumbnail(false);
      }
    },
    [updatePlanData]
  );

  return (
    <div className="bg-white dark:bg-shoorah-darkBgTabColor rounded-lg p-6 ">
      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">Plan Details</h3>

      <div className="grid lg:gap-6 gap-4 md:grid-cols-2 grid-cols-1">
        {/* Plan Title */}
        <div>
          <InputWithLabel
            label="Plan Title"
            placeholder="eg.Full Abs and Core"
            onChange={handleChange}
            errorMargin="mt-1"
            labelMargin="mb-1"
            errorSize="text-[12px]"
            isLengthValidate={true}
            limit={100}
            labelClassName="text-sm font-medium text-gray-700 dark:text-shoorah-darkBg"
            value={planData.plan_name}
            name="plan_name"
            id="plan_name"
            error={errors?.plan_name}
            isRequired={true}
            disabled={isView}
          />
        </div>

        {/* Difficulty Level */}
        <div>
          <label className="font-medium text-gray-700 dark:text-shoorah-darkBg mb-1 block text-sm">
            Difficulty <span className="text-red-400">&#42;</span>
          </label>
          <CommonSelect
            disabled={isView}
            name="difficulty"
            onChange={(e) => handleSelectChange('difficulty', e)}
            value={DIFFICULTY_LEVELS?.find((option) => option?.value === planData.difficulty)}
            options={DIFFICULTY_LEVELS}
            placeholder="Level of difficulty"
          />
          {errors?.difficulty && (
            <span className="text-red-500 text-[12px] mt-1 block">{errors.difficulty}</span>
          )}
        </div>
      </div>

      {/* Plan Description */}
      <div className="mt-4">
        <TextareaWithLabel
          label="Plan Description"
          placeholder="Enter description: e.g A fast-paced, full body routine..."
          onChange={handleChange}
          errorMargin="mt-1"
          labelMargin="mb-1"
          errorSize="text-[12px]"
          isLengthValidate={true}
          limit={500}
          labelClassName="text-sm font-medium text-gray-700 dark:text-shoorah-darkBg"
          value={planData.plan_description}
          name="plan_description"
          id="plan_description"
          error={errors?.plan_description}
          isRequired={true}
          disabled={isView}
          rows={4}
        />
      </div>

      {/* Thumbnail Upload */}
      {!isView && (
        <div className="mt-4">
          <label className="font-medium text-gray-700 dark:text-shoorah-darkBg mb-1 block text-sm">
            Thumbnail (Upload 512px * 512px image) (Max image size 1MB)
          </label>
          <div className="mt-1">
            {previewThumbnailImage ? (
              <>
                <div className="relative">
                  <div className="h-10 flex items-center justify-between gap-3 w-full rounded-lg px-4 py-[8px] text-sm text-shoorah-lightBlack dark:border-shoorah-darkBorder dark:text-shoorah-darkBg appearance-none placeholder-gray-400 bg-shoorah-normanGray dark:bg-shoorah-darkBgColor border-[1.5px] border-shoorah-borderGray">
                    <p className="truncate mr-6">
                      {previewThumbnailImage?.split('/')[
                        previewThumbnailImage?.split('/')?.length - 1
                      ] || 'Thumbnail uploaded'}
                    </p>
                  </div>
                  <XMarkIcon
                    className="absolute top-1/2 transform -translate-y-1/2 right-[10px] w-[20px] text-red-500 cursor-pointer"
                    onClick={() => {
                      setPreviewThumbnailImage('');
                      setUserUploadedNewImage(false);
                      updatePlanData({ thumbnail_url: '', thumbnailFile: null });
                    }}
                  />
                </div>
                <div className="mx-auto border rounded-lg mt-2 text-center">
                  <LazyLoadImageProp
                    imageSrc={previewThumbnailImage}
                    className="mx-auto max-h-[250px] rounded-lg"
                  />
                </div>
              </>
            ) : (
              <DragDropFileInput
                accept=".jpg,.jpeg,.png,.gif"
                hint="Supports: JPG, JPEG, PNG, GIF (Max 1MB)"
                icon={<Icons.videoUploadIcon className="mb-5 text-shoorah-primary" />}
                onChange={handleThumbnailUpload}
                disabled={uploadingThumbnail}
                uploading={uploadingThumbnail}
                error={errors?.thumbnail_url}
              />
            )}
          </div>
        </div>
      )}

      <div className="grid lg:gap-6 gap-4 md:grid-cols-2 grid-cols-1 mt-4">
        {/* Workout Location */}
        <div>
          <label className="font-medium text-gray-700 dark:text-shoorah-darkBg mb-1 block text-sm">
            Workout Location <span className="text-red-400">&#42;</span>
          </label>
          <CommonSelect
            disabled={isView}
            name="location"
            onChange={(e) => handleSelectChange('location', e)}
            value={LOCATIONS?.find((option) => option?.value === planData.location)}
            options={LOCATIONS}
            placeholder="Location"
          />
          {errors?.location && (
            <span className="text-red-500 text-[12px] mt-1 block">{errors.location}</span>
          )}
        </div>

        {/* Workout Focus */}
        <div>
          <label className="font-medium text-gray-700 dark:text-shoorah-darkBg mb-1 block text-sm">
            Workout Focus <span className="text-red-400">&#42;</span>
          </label>
          <CommonSelect
            disabled={isView}
            name="workout_focus"
            onChange={(e) => handleSelectChange('workout_focus', e)}
            value={FOCUS_OPTIONS?.find((option) => option?.value === planData.workout_focus)}
            options={FOCUS_OPTIONS}
            placeholder="Focus"
          />
          {errors?.workout_focus && (
            <span className="text-red-500 text-[12px] mt-1 block">{errors.workout_focus}</span>
          )}
        </div>
      </div>
    </div>
  );
};

export default WorkoutPlanDetailsTab;
export { DIFFICULTY_LEVELS, LOCATIONS, FOCUS_OPTIONS };
